import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Plus,
  Filter,
  Edit2,
  Trash2,
  Eye,
  Settings,
  Check,
  Clock,
  CheckCircle2,
  AlertCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  SortAsc,
  SortDesc,
  GripVertical,
  Eraser,
  RotateCcw
} from 'lucide-react';

const cn = (...classes) => classes.filter(Boolean).join(' ');

const extractText = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (React.isValidElement(node) && node.props && node.props.children) {
    return extractText(node.props.children);
  }
  return '';
};

export const GlobalTable = ({
  title,
  subtitle,
  data = [],
  columns = [],
  onAdd,
  onCreate,
  onEdit,
  onDelete,
  onView,
  searchPlaceholder = 'Buscar en todos los registros...',
  isLoading = false,
  storageKey,
  extraActions,
  extraRightActions,
  searchValue,
  onSearchChange,
  pagination,
  pageSize = 15
}) => {
  const handleAddAction = onAdd || onCreate;

  // Normalize columns so each has a stable `colKey`
  const normalizedColumns = React.useMemo(
    () =>
      columns.map((col, idx) => ({
        ...col,
        colKey: String(col.key || col.accessor || col.header || `col_${idx}`)
      })),
    [columns]
  );

  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const searchTerm = searchValue !== undefined ? searchValue : internalSearchTerm;
  const setSearchTerm = (val) => {
    if (onSearchChange) {
      onSearchChange(val);
    } else {
      setInternalSearchTerm(val);
    }
    setInternalPage(1);
  };

  const [internalPage, setInternalPage] = useState(1);

  // Storage Key for LocalStorage Persistence
  const effectiveKey = `atecma_tbl_cfg_${
    storageKey ||
    (title ? title.toLowerCase().replace(/[^a-z0-9]/g, '_') : '') ||
    (typeof window !== 'undefined' ? window.location.pathname.replace(/[^a-z0-9]/g, '_') : 'default')
  }`;

  const savedConfigRef = useRef(null);
  if (savedConfigRef.current === null) {
    try {
      const raw = localStorage.getItem(effectiveKey);
      savedConfigRef.current = raw ? JSON.parse(raw) : {};
    } catch {
      savedConfigRef.current = {};
    }
  }

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const validKeys = normalizedColumns.map((c) => c.colKey);
    const saved = savedConfigRef.current?.visibleColumns;
    const savedOrder = savedConfigRef.current?.columnOrder;
    if (saved && Array.isArray(saved) && saved.length >= Math.min(2, validKeys.length)) {
      const filtered = saved.filter((k) => validKeys.includes(k));
      const newlyAdded = validKeys.filter(
        (k) => !saved.includes(k) && (!savedOrder || !savedOrder.includes(k))
      );
      if (filtered.length + newlyAdded.length > 0) {
        return new Set([...filtered, ...newlyAdded]);
      }
    }
    return new Set(validKeys);
  });

  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);

  // Column order state (Drag & Drop)
  const [columnOrder, setColumnOrder] = useState(() => {
    const keys = normalizedColumns.map((c) => c.colKey);
    const saved = savedConfigRef.current?.columnOrder;
    if (saved && Array.isArray(saved)) {
      const existing = saved.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !existing.includes(k));
      return [...existing, ...added];
    }
    return keys;
  });

  const [draggedColKey, setDraggedColKey] = useState(null);
  const [dragOverColKey, setDragOverColKey] = useState(null);

  // Sorting state
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  // Column widths state (Resizing)
  const [columnWidths, setColumnWidths] = useState(() => {
    const saved = savedConfigRef.current?.columnWidths;
    return saved && typeof saved === 'object' ? saved : {};
  });
  const resizingColRef = useRef(null);

  const handleResizeStart = (e, keyStr, currentWidth) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = currentWidth || 150;

    resizingColRef.current = { key: keyStr, startX, startWidth };

    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      const newWidth = Math.max(70, startWidth + diff);
      setColumnWidths((prev) => ({
        ...prev,
        [keyStr]: newWidth
      }));
    };

    const handleMouseUp = () => {
      resizingColRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Persist table config
  useEffect(() => {
    try {
      const payload = {
        visibleColumns: Array.from(visibleColumns),
        columnOrder,
        columnWidths
      };
      localStorage.setItem(effectiveKey, JSON.stringify(payload));
    } catch (e) {
      // ignore storage errors
    }
  }, [effectiveKey, visibleColumns, columnOrder, columnWidths]);

  const resetAllTableConfig = () => {
    const defaultKeys = normalizedColumns.map((c) => c.colKey);
    setVisibleColumns(new Set(defaultKeys));
    setColumnOrder(defaultKeys);
    setColumnWidths({});
    try {
      localStorage.removeItem(effectiveKey);
    } catch (e) {}
  };

  // Per-column filters
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilterPopover, setActiveFilterPopover] = useState(null);
  const filterPopoverRef = useRef(null);

  // Sync when columns prop updates
  useEffect(() => {
    const keys = normalizedColumns.map((c) => c.colKey);
    setVisibleColumns((prev) => {
      if (prev.size === 0) return new Set(keys);
      const next = new Set();
      const savedOrder = savedConfigRef.current?.columnOrder;
      normalizedColumns.forEach((c) => {
        if (prev.has(c.colKey) || !savedOrder || !savedOrder.includes(c.colKey)) {
          next.add(c.colKey);
        }
      });
      return next.size === 0 ? new Set(keys) : next;
    });
    setColumnOrder((prevOrder) => {
      const existing = prevOrder.filter((k) => keys.includes(k));
      const added = keys.filter((k) => !existing.includes(k));
      return [...existing, ...added];
    });
  }, [normalizedColumns]);

  // Close popovers on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colPickerRef.current && !colPickerRef.current.contains(event.target)) {
        setShowColPicker(false);
      }
      if (filterPopoverRef.current && !filterPopoverRef.current.contains(event.target)) {
        setActiveFilterPopover(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Drag & Drop handlers
  const handleDragStart = (e, keyStr) => {
    setDraggedColKey(keyStr);
    e.dataTransfer.setData('text/plain', keyStr);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, keyStr) => {
    e.preventDefault();
    if (draggedColKey && draggedColKey !== keyStr) {
      setDragOverColKey(keyStr);
    }
  };

  const handleDrop = (e, targetKeyStr) => {
    e.preventDefault();
    if (!draggedColKey || draggedColKey === targetKeyStr) {
      setDraggedColKey(null);
      setDragOverColKey(null);
      return;
    }

    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const fromIndex = newOrder.indexOf(draggedColKey);
      const toIndex = newOrder.indexOf(targetKeyStr);
      if (fromIndex !== -1 && toIndex !== -1) {
        newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, draggedColKey);
      }
      return newOrder;
    });

    setDraggedColKey(null);
    setDragOverColKey(null);
  };

  const handleDragEnd = () => {
    setDraggedColKey(null);
    setDragOverColKey(null);
  };

  const toggleColumn = (key) => {
    const newVisible = new Set(visibleColumns);
    if (newVisible.has(key)) {
      if (newVisible.size > 1) newVisible.delete(key);
    } else {
      newVisible.add(key);
    }
    setVisibleColumns(newVisible);
  };

  const showAllColumns = () => {
    setVisibleColumns(new Set(normalizedColumns.map((c) => c.colKey)));
  };

  const handleHeaderSort = (keyStr) => {
    if (sortKey === keyStr) {
      if (sortOrder === 'asc') setSortOrder('desc');
      else if (sortOrder === 'desc') {
        setSortKey(null);
        setSortOrder(null);
      }
    } else {
      setSortKey(keyStr);
      setSortOrder('asc');
    }
  };

  const getRawValue = (item, col) => {
    const propKey = col.key || col.accessor;
    if (propKey && item && item[propKey] !== undefined && item[propKey] !== null) {
      return item[propKey];
    }
    if (col.render) {
      const rendered = col.render(item);
      return extractText(rendered);
    }
    return '';
  };

  const getCellString = (item, col) => {
    const val = getRawValue(item, col);
    if (val === null || val === undefined) return '';
    if (typeof val === 'object') {
      return extractText(val).toLowerCase();
    }
    return String(val).toLowerCase();
  };

  // 1. Filter data (Global + Per-Column)
  let filteredData = (Array.isArray(data) ? data : []).filter((item) => {
    if (searchTerm.trim() !== '') {
      const q = searchTerm.toLowerCase();
      const matchCol = normalizedColumns.some((col) => getCellString(item, col).includes(q));
      const matchRaw = Object.values(item || {}).some(
        (v) => (typeof v === 'string' || typeof v === 'number') && String(v).toLowerCase().includes(q)
      );
      if (!matchCol && !matchRaw) return false;
    }

    for (const [colKey, filterVal] of Object.entries(columnFilters)) {
      if (filterVal && filterVal.trim() !== '') {
        const col = normalizedColumns.find((c) => c.colKey === colKey);
        if (col) {
          const cellStr = getCellString(item, col);
          if (!cellStr.includes(filterVal.toLowerCase())) {
            return false;
          }
        }
      }
    }

    return true;
  });

  // 2. Sort data
  if (sortKey && sortOrder) {
    const col = normalizedColumns.find((c) => c.colKey === sortKey);
    if (col) {
      filteredData = [...filteredData].sort((a, b) => {
        const valA = getRawValue(a, col);
        const valB = getRawValue(b, col);

        if (valA === valB) return 0;
        if (valA === null || valA === undefined || valA === '') return 1;
        if (valB === null || valB === undefined || valB === '') return -1;

        let comp = 0;
        const numA = Number(valA);
        const numB = Number(valB);
        if (!isNaN(numA) && !isNaN(numB) && typeof valA !== 'boolean' && typeof valB !== 'boolean') {
          comp = numA - numB;
        } else {
          comp = String(valA).localeCompare(String(valB), undefined, {
            numeric: true,
            sensitivity: 'base'
          });
        }

        return sortOrder === 'asc' ? comp : -comp;
      });
    }
  }

  // Active ordered columns
  const activeColumns = columnOrder
    .map((keyStr) => normalizedColumns.find((c) => c.colKey === keyStr))
    .filter((col) => !!col && visibleColumns.has(col.colKey));

  const hasActiveFiltersOrSort =
    Object.values(columnFilters).some((v) => v && v.trim() !== '') ||
    sortKey !== null ||
    searchTerm.trim() !== '';

  const clearAllFilters = () => {
    setColumnFilters({});
    setSortKey(null);
    setSortOrder(null);
    setSearchTerm('');
    setInternalPage(1);
  };

  // Pagination calculation (supports external `pagination` or internal slicing)
  const useExternalPagination = Boolean(pagination && pagination.onPageChange);
  const totalPages = useExternalPagination
    ? Math.max(1, pagination.totalPages || 1)
    : Math.max(1, Math.ceil(filteredData.length / pageSize));
  const currentPage = useExternalPagination
    ? pagination.currentPage || 1
    : Math.min(internalPage, totalPages);

  const displayedData = useExternalPagination
    ? filteredData
    : filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    if (useExternalPagination) {
      pagination.onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  return (
    <div className="flex flex-col h-full w-full gap-2.5">
      {/* Header & Controls Bar */}
      <div className="bg-white p-2.5 rounded shadow-sm border border-slate-200 flex flex-row justify-between items-center gap-3 z-20">
        <div className="flex items-center gap-2 relative flex-wrap sm:flex-nowrap w-full justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            {title && (
              <div className="flex flex-col">
                <h2 className="text-sm md:text-base font-bold text-[#005f88] tracking-tight truncate">
                  {title}
                </h2>
                {subtitle && <p className="text-xs text-slate-500 truncate">{subtitle}</p>}
              </div>
            )}
            {extraActions}
          </div>

          <div className="flex items-center gap-2 ml-auto">
            {extraRightActions}

            {/* Global Search Input */}
            <div className="relative w-56 sm:w-64">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-8 py-1.5 text-sm bg-white border border-[#b8d6e7] rounded focus:ring-1 focus:ring-[#0A6ED1] focus:border-[#0A6ED1] outline-none transition-all text-slate-700"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Gear / Column Config Popover */}
            <div className="relative" ref={colPickerRef}>
              <button
                type="button"
                onClick={() => setShowColPicker(!showColPicker)}
                className={cn(
                  'p-1.5 border rounded transition-colors flex items-center justify-center gap-1.5 text-xs font-semibold',
                  showColPicker
                    ? 'bg-[#0A6ED1] border-[#0A6ED1] text-white shadow-sm'
                    : 'bg-white border-[#b8d6e7] text-slate-600 hover:bg-slate-50 hover:text-[#0A6ED1]'
                )}
                title="Configurar columnas de la tabla"
              >
                <Settings
                  className={cn(
                    'w-4 h-4 transition-transform duration-300',
                    showColPicker && 'rotate-45'
                  )}
                />
              </button>

              {showColPicker && (
                <div className="absolute right-0 mt-1.5 w-64 bg-white rounded-lg shadow-xl border border-slate-200 z-50 py-2">
                  <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                      <Settings className="w-3.5 h-3.5 text-[#0A6ED1]" /> Columnas
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={showAllColumns}
                        className="p-1 hover:bg-blue-50 text-[#0A6ED1] rounded transition-colors"
                        title="Mostrar todas las columnas"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={resetAllTableConfig}
                        className="p-1 hover:bg-amber-50 text-amber-600 rounded transition-colors"
                        title="Restablecer visibilidad, orden y anchos por defecto"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1 divide-y divide-slate-50">
                    {normalizedColumns
                      .filter((col) => !col.hideFilter)
                      .map((col) => {
                        const isVisible = visibleColumns.has(col.colKey);
                        return (
                          <button
                            type="button"
                            key={col.colKey}
                            onClick={() => toggleColumn(col.colKey)}
                            className="w-full text-left px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 flex items-center justify-between transition-colors"
                          >
                            <span className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={isVisible}
                                readOnly
                                className="rounded border-slate-300 text-[#0A6ED1] focus:ring-[#0A6ED1] h-3.5 w-3.5 pointer-events-none"
                              />
                              {col.header}
                            </span>
                            {isVisible && <Check className="w-3.5 h-3.5 text-[#0A6ED1]" />}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Reset All Filters Button */}
            {hasActiveFiltersOrSort && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="p-1.5 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded transition-colors flex items-center justify-center shadow-sm"
                title="Limpiar todos los filtros y ordenamiento"
              >
                <Eraser className="w-4 h-4 text-amber-700" />
              </button>
            )}

            {/* Add / Create Button */}
            {handleAddAction && (
              <button
                type="button"
                onClick={handleAddAction}
                className="inline-flex items-center justify-center p-1.5 bg-[#006699] text-white rounded hover:bg-[#0854A0] transition-all ml-1 shadow-sm"
                title="Crear Nuevo Registro"
              >
                <Plus className="w-4 h-4 font-bold" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 flex flex-col bg-white rounded shadow-sm border border-[#005f88] overflow-hidden min-h-0">
        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full text-left text-sm whitespace-nowrap border-collapse">
            <thead className="bg-[#005f88] text-white select-none sticky top-0 z-10 shadow-xs">
              <tr>
                {activeColumns.map((col) => {
                  const keyStr = col.colKey;
                  const isSorted = sortKey === keyStr;
                  const filterVal = columnFilters[keyStr] || '';
                  const hasFilter = filterVal.trim() !== '';
                  const isPopoverOpen = activeFilterPopover === keyStr;

                  return (
                    <th
                      key={keyStr}
                      draggable={!col.hideFilter}
                      onDragStart={(e) => handleDragStart(e, keyStr)}
                      onDragOver={(e) => handleDragOver(e, keyStr)}
                      onDrop={(e) => handleDrop(e, keyStr)}
                      onDragEnd={handleDragEnd}
                      style={{
                        width: columnWidths[keyStr]
                          ? `${columnWidths[keyStr]}px`
                          : col.width
                          ? `${col.width}px`
                          : undefined,
                        minWidth: columnWidths[keyStr]
                          ? `${columnWidths[keyStr]}px`
                          : col.minWidth
                          ? `${col.minWidth}px`
                          : '120px'
                      }}
                      className={cn(
                        'px-3 py-2 font-bold text-xs uppercase tracking-wider border-r border-[#004a6b] last:border-r-0 relative group select-none transition-all',
                        col.hideFilter ? 'px-1 text-center' : 'cursor-grab active:cursor-grabbing',
                        draggedColKey === keyStr && 'opacity-40 bg-[#004a6b]',
                        dragOverColKey === keyStr && 'bg-[#0073ab] border-l-2 border-l-amber-300'
                      )}
                    >
                      <div
                        className={cn(
                          'flex items-center gap-1',
                          col.hideFilter ? 'justify-center' : 'justify-between'
                        )}
                      >
                        {/* Drag Handle */}
                        {!col.hideFilter && (
                          <span
                            className="opacity-30 group-hover:opacity-100 transition-opacity cursor-grab text-cyan-200 mr-0.5"
                            title="Arrastrar para reordenar columna"
                          >
                            <GripVertical className="w-3.5 h-3.5 shrink-0" />
                          </span>
                        )}

                        {/* Header Label & Sort Button */}
                        {col.hideFilter ? (
                          <span className="truncate text-center font-mono text-[11px] block">
                            {col.header}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleHeaderSort(keyStr)}
                            className="flex items-center gap-1.5 hover:text-cyan-200 transition-colors flex-1 text-left py-0.5 outline-none font-bold"
                            title="Clic para ordenar A-Z / Z-A | Arrastre para reordenar"
                          >
                            <span className="truncate">{col.header}</span>
                            {isSorted ? (
                              sortOrder === 'asc' ? (
                                <ArrowUp className="w-3.5 h-3.5 text-amber-300 font-extrabold flex-shrink-0" />
                              ) : (
                                <ArrowDown className="w-3.5 h-3.5 text-amber-300 font-extrabold flex-shrink-0" />
                              )
                            ) : (
                              <ArrowUpDown className="w-3 h-3 text-cyan-200/50 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                            )}
                          </button>
                        )}

                        {/* Per-Column Filter Funnel Button */}
                        {!col.hideFilter && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveFilterPopover(isPopoverOpen ? null : keyStr);
                            }}
                            className={cn(
                              'p-1 rounded transition-colors flex-shrink-0',
                              hasFilter
                                ? 'bg-amber-400 text-slate-900 font-bold shadow-sm'
                                : 'hover:bg-[#004a6b] text-cyan-200/70 hover:text-white'
                            )}
                            title={`Filtrar por ${col.header}`}
                          >
                            <Filter className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>

                      {/* Excel-Style Filter Popover */}
                      {isPopoverOpen && (
                        <div
                          ref={filterPopoverRef}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute left-0 top-full mt-1 w-64 bg-white text-slate-800 rounded-md shadow-2xl border border-slate-300 z-50 p-3 font-normal normal-case text-xs"
                        >
                          <div className="flex items-center justify-between pb-2 mb-2 border-b border-slate-200 font-bold text-slate-700">
                            <span className="flex items-center gap-1.5 text-[#0A6ED1]">
                              <Filter className="w-3.5 h-3.5" />
                              Filtro: {col.header}
                            </span>
                            <button
                              type="button"
                              onClick={() => setActiveFilterPopover(null)}
                              className="p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>

                          {/* Quick Sort */}
                          <div className="space-y-1 mb-3">
                            <button
                              type="button"
                              onClick={() => {
                                setSortKey(keyStr);
                                setSortOrder('asc');
                                setActiveFilterPopover(null);
                              }}
                              className={cn(
                                'w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-xs transition-colors',
                                isSorted && sortOrder === 'asc'
                                  ? 'bg-blue-50 font-bold text-[#0A6ED1]'
                                  : 'hover:bg-slate-100 text-slate-700'
                              )}
                            >
                              <SortAsc className="w-3.5 h-3.5 text-slate-500" />
                              Ordenar A → Z (Ascendente)
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setSortKey(keyStr);
                                setSortOrder('desc');
                                setActiveFilterPopover(null);
                              }}
                              className={cn(
                                'w-full text-left px-2 py-1.5 rounded flex items-center gap-2 text-xs transition-colors',
                                isSorted && sortOrder === 'desc'
                                  ? 'bg-blue-50 font-bold text-[#0A6ED1]'
                                  : 'hover:bg-slate-100 text-slate-700'
                              )}
                            >
                              <SortDesc className="w-3.5 h-3.5 text-slate-500" />
                              Ordenar Z → A (Descendente)
                            </button>
                          </div>

                          {/* Column Search Input */}
                          <div className="space-y-1.5">
                            <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                              Buscar en esta columna:
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                autoFocus
                                placeholder="Escriba para buscar..."
                                value={filterVal}
                                onChange={(e) => {
                                  const newVal = e.target.value;
                                  setColumnFilters((prev) => ({
                                    ...prev,
                                    [keyStr]: newVal
                                  }));
                                  setInternalPage(1);
                                }}
                                className="w-full px-2.5 py-1.5 pr-7 text-xs bg-slate-50 border border-slate-300 rounded focus:bg-white focus:border-[#0A6ED1] outline-none text-slate-800"
                              />
                              {hasFilter && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setColumnFilters((prev) => {
                                      const c = { ...prev };
                                      delete c[keyStr];
                                      return c;
                                    });
                                    setInternalPage(1);
                                  }}
                                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5"
                                  title="Limpiar filtro de columna"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          </div>

                          {hasFilter && (
                            <div className="mt-3 pt-2 border-t border-slate-100 flex justify-end">
                              <button
                                type="button"
                                onClick={() => {
                                  setColumnFilters((prev) => {
                                    const c = { ...prev };
                                    delete c[keyStr];
                                    return c;
                                  });
                                  setActiveFilterPopover(null);
                                  setInternalPage(1);
                                }}
                                className="text-[11px] font-bold text-red-600 hover:underline"
                              >
                                Limpiar este filtro
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Column Right-Edge Resizer */}
                      <div
                        onMouseDown={(e) => {
                          const thElem = e.currentTarget.parentElement;
                          const currentW = thElem ? thElem.getBoundingClientRect().width : 150;
                          handleResizeStart(e, keyStr, currentW);
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute right-0 top-0 bottom-0 w-2.5 cursor-col-resize hover:bg-amber-400 group-hover:bg-cyan-400/50 z-20 transition-colors"
                        title="Arrastrar borde para anchar o reducir esta columna"
                      />
                    </th>
                  );
                })}
                {(onEdit || onDelete || onView) && (
                  <th className="px-4 py-2 text-center font-bold text-xs uppercase tracking-wider border-r border-[#004a6b] last:border-r-0 w-24">
                    Acciones
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={activeColumns.length + (onEdit || onDelete || onView ? 1 : 0)}
                    className="px-4 py-8 text-center text-slate-500 border border-slate-200"
                  >
                    <div className="animate-pulse flex flex-col items-center">
                      <div className="h-6 w-6 rounded-full border-2 border-[#0A6ED1] border-t-transparent animate-spin mb-2"></div>
                      Cargando datos...
                    </div>
                  </td>
                </tr>
              ) : displayedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={activeColumns.length + (onEdit || onDelete || onView ? 1 : 0)}
                    className="px-4 py-12 text-center text-slate-400"
                  >
                    <div className="flex flex-col items-center justify-center">
                      <Search className="w-6 h-6 text-slate-300 mb-2" />
                      <p className="font-semibold text-slate-500 text-sm">
                        No se encontraron registros con los filtros actuales
                      </p>
                      {hasActiveFiltersOrSort && (
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="mt-2 text-xs text-[#0A6ED1] font-bold hover:underline"
                        >
                          Limpiar todos los filtros
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                displayedData.map((item, rowIndex) => (
                  <tr
                    key={item.id || item.userid || rowIndex}
                    className={`${
                      rowIndex % 2 === 0 ? 'bg-white' : 'bg-[#eaf4f8]'
                    } hover:bg-[#d5e8f2] transition-colors duration-75 group`}
                  >
                    {activeColumns.map((col) => {
                      const kStr = col.colKey;
                      const propKey = col.key || col.accessor;
                      const cellRaw = propKey ? item[propKey] : undefined;

                      return (
                        <td
                          key={kStr}
                          style={{
                            width: columnWidths[kStr]
                              ? `${columnWidths[kStr]}px`
                              : col.width
                              ? `${col.width}px`
                              : undefined,
                            minWidth: columnWidths[kStr]
                              ? `${columnWidths[kStr]}px`
                              : col.minWidth
                              ? `${col.minWidth}px`
                              : '120px'
                          }}
                          className={cn(
                            'px-4 py-1.5 text-slate-700 text-[13px] font-medium border-r border-b border-[#cde0eb] last:border-r-0',
                            col.hideFilter ? 'p-1 text-center select-none' : 'truncate'
                          )}
                        >
                          {col.render ? (
                            col.render(item, cellRaw)
                          ) : (kStr.toLowerCase() === 'estado' ||
                              kStr.toLowerCase() === 'status') &&
                            typeof cellRaw === 'string' ? (
                            <span
                              className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide border ${
                                cellRaw.toLowerCase().includes('activ') ||
                                cellRaw.toLowerCase() === 'vigente' ||
                                cellRaw.toLowerCase() === 'ok'
                                  ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                                  : cellRaw.toLowerCase().includes('pend') ||
                                    cellRaw.toLowerCase().includes('prox')
                                  ? 'bg-amber-100 text-amber-800 border-amber-200'
                                  : 'bg-red-100 text-red-800 border-red-200'
                              }`}
                            >
                              {cellRaw.toLowerCase().includes('activ') ||
                              cellRaw.toLowerCase() === 'vigente' ||
                              cellRaw.toLowerCase() === 'ok' ? (
                                <CheckCircle2 className="w-3.5 h-3.5" />
                              ) : cellRaw.toLowerCase().includes('pend') ||
                                cellRaw.toLowerCase().includes('prox') ? (
                                <Clock className="w-3.5 h-3.5" />
                              ) : (
                                <AlertCircle className="w-3.5 h-3.5" />
                              )}
                              {cellRaw}
                            </span>
                          ) : (
                            cellRaw
                          )}
                        </td>
                      );
                    })}
                    {(onEdit || onDelete || onView) && (
                      <td className="px-2 py-1 text-center border-r border-b border-[#cde0eb] last:border-r-0">
                        <div className="flex items-center justify-center gap-1">
                          {onView && (
                            <button
                              type="button"
                              onClick={() => onView(item)}
                              className="p-1 text-slate-500 hover:text-[#0A6ED1] hover:bg-blue-100 rounded transition-colors"
                              title="Ver detalle"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {onEdit && (
                            <button
                              type="button"
                              onClick={() => onEdit(item)}
                              className="p-1 text-slate-500 hover:text-[#0A6ED1] hover:bg-blue-100 rounded transition-colors"
                              title="Editar"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          )}
                          {onDelete && (
                            <button
                              type="button"
                              onClick={() => onDelete(item)}
                              className="p-1 text-slate-500 hover:text-red-600 hover:bg-red-100 rounded transition-colors"
                              title="Eliminar"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Ocean Blue Pagination Footer */}
        <div className="px-4 py-2.5 border-t border-[#005f88] bg-[#eaf4f8] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-[13px] text-[#005f88] font-medium">
            Mostrando{' '}
            <span className="font-semibold">
              {displayedData.length > 0 ? (currentPage - 1) * (useExternalPagination ? displayedData.length : pageSize) + 1 : 0}
            </span>{' '}
            a{' '}
            <span className="font-semibold">
              {useExternalPagination
                ? displayedData.length
                : Math.min(currentPage * pageSize, filteredData.length)}
            </span>{' '}
            de <span className="font-semibold">{filteredData.length}</span> resultados
            {useExternalPagination && totalPages > 1 && (
              <span className="ml-2 text-xs text-[#004a6b]">
                (Página {currentPage} de {totalPages})
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5 font-sans">
            <button
              type="button"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className={cn(
                'px-3 py-1.5 rounded text-[13px] font-medium transition-colors',
                currentPage <= 1
                  ? 'bg-[#005f88]/50 text-white/70 cursor-not-allowed'
                  : 'bg-[#005f88] text-white hover:bg-[#004a6b]'
              )}
            >
              Previous
            </button>

            <button
              type="button"
              className="w-7 h-7 flex items-center justify-center rounded bg-[#005f88] text-white font-bold text-[13px] hover:bg-[#004a6b] transition-colors"
            >
              {currentPage}
            </button>

            <button
              type="button"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className={cn(
                'px-3 py-1.5 rounded text-[13px] font-medium transition-colors',
                currentPage >= totalPages
                  ? 'bg-[#005f88]/50 text-white/70 cursor-not-allowed'
                  : 'bg-[#005f88] text-white hover:bg-[#004a6b]'
              )}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalTable;
